/**
 * Siloed Recoil state for BrandedEmbeddablePlaylist.
 *
 * Intentionally NOT reusing MVVM/State/embeddablePlaylist atoms — keeping the
 * branded playlist fully isolated so changes to the legacy partner playlist
 * state can't regress us, and vice versa.
 */
import { atom } from "recoil";

import type { PlaylistClip } from "../../@types";
import type { PlaybackPhase, ResolvedSponsorConfig } from "./@types";

export const BrandedPlaylistClipsState = atom<PlaylistClip[] | null>({
  key: "BrandedPlaylistClipsState",
  default: null,
});

/**
 * True while the initial playlist fetch is in flight. The shell uses this to
 * render a skeleton instead of the bare empty player frame (which previously
 * showed `'s highlight` / `0 views` placeholders for the moment between mount
 * and the first response landing). Stays `true` until the fetch resolves OR
 * errors — the empty-results case is signaled by clips being an empty array
 * with loading false.
 */
export const BrandedPlaylistLoadingState = atom<boolean>({
  key: "BrandedPlaylistLoadingState",
  default: true,
});

export const BrandedActiveClipState = atom<PlaylistClip | null>({
  key: "BrandedActiveClipState",
  default: null,
});

export const BrandedActiveClipIndexState = atom<number>({
  key: "BrandedActiveClipIndexState",
  default: 0,
});

/** Driven by BrandedPlayer's <video> timeupdate; ms since clip started. */
export const BrandedPlaybackElapsedMsState = atom<number>({
  key: "BrandedPlaybackElapsedMsState",
  default: 0,
});

export const BrandedPlaybackPhaseState = atom<PlaybackPhase>({
  key: "BrandedPlaybackPhaseState",
  default: "intro",
});

export const BrandedSponsorConfigState = atom<ResolvedSponsorConfig | null>({
  key: "BrandedSponsorConfigState",
  default: null,
});

/** User can dismiss the top banner; keep that flag scoped to the active clip. */
export const BrandedTopBannerDismissedState = atom<boolean>({
  key: "BrandedTopBannerDismissedState",
  default: false,
});

/**
 * Whether the player frame is currently in browser fullscreen. Tracked
 * via a document `fullscreenchange` listener wired up in
 * BrandedPlayer/ViewModel. Several overlays (top-left watermark, top-
 * right banner, endcard title) scale up dramatically when this is true
 * to read at fullscreen viewing distance — the CSS `:fullscreen`
 * pseudo-class proved unreliable inside the iframe context, so we
 * drive it from React state instead.
 */
export const BrandedFullscreenState = atom<boolean>({
  key: "BrandedFullscreenState",
  default: false,
});

/** Selected geo (for V1 demo, default "*"). Drives sponsor resolution. */
export const BrandedViewerGeoState = atom<string>({
  key: "BrandedViewerGeoState",
  default: "*",
});

/**
 * Layout variant for the branded iframe. Two modes:
 *
 *   "sideRail"        Default. Player on the left, playlist column on
 *                     the right at desktop widths; stacks player-on-top /
 *                     rail-below at mobile widths. This is the shipping
 *                     HLTV 638×226 in-article embed.
 *   "bottomPlaylist"  Player full-width on top, playlist as a horizontal
 *                     card strip below. Used for the "Desktop — Bottom
 *                     Playlist" preview variant we send to publishers
 *                     who want a less-claustrophobic alternative — the
 *                     player can run at the full iframe width and the
 *                     rail tucks under it. Driven from the iframe URL
 *                     param `?layout=bottomPlaylist`.
 *
 * The atom is hydrated once at mount from the URL (see useBrandedPlaylist
 * in ViewModel.tsx) so styled components can consume it without
 * reading window.location.search themselves.
 */
export type BrandedLayoutVariant = "sideRail" | "bottomPlaylist";

export const BrandedLayoutVariantState = atom<BrandedLayoutVariant>({
  key: "BrandedLayoutVariantState",
  default: "sideRail",
});

/**
 * Active category filter for the playlist tray.
 *
 * Per Figma 1:178 the header presents a `Show:` prefix followed by a row
 * of category pills (TRENDING N, ACES N, …). Clicking a category narrows
 * the tray to clips matching that category's predicate; clicking the
 * same pill again clears the filter back to TRENDING (everything).
 *
 * The currently-playing clip is always kept in the tray regardless of
 * the active filter, so the user doesn't lose context when activating
 * a filter that the now-playing clip doesn't match.
 *
 * Categories are deliberately a small enum (not freeform tag labels) so
 * the header pill set stays bounded and the predicate logic stays
 * exhaustive — extending the design with a new category requires a
 * predicate addition in BrandedEmbeddablePlaylist.tsx.
 */
export type BrandedPlaylistCategory = "aces" | "clutches" | "fourk" | "threek";

export type BrandedPlaylistFilter = {
  category: BrandedPlaylistCategory;
} | null;

export const BrandedPlaylistFilterState = atom<BrandedPlaylistFilter>({
  key: "BrandedPlaylistFilterState",
  default: null,
});
